import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Typography, alpha, useTheme, useMediaQuery } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import { useColorMode } from 'src/theme/ThemeProvider';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { useAppDispatch, useAppSelector } from 'src/store';
import { addMessage } from 'src/features/account/conversations.slice';
import feedbacksService, { FeedbackCreate, FeedbackUpdate } from 'src/services/feedbacks.service';
import { trackAnalytics } from 'src/features/analytics/analytics.slice';
import { fetchChunks } from 'src/features/chunks/chunks.slice';
import { dateTimeFormatter } from 'src/libs/dateTimeFormatter';
import RescheduleDelivery from './RescheduleDelivery';
import CircularProgress from '@mui/material/CircularProgress';

interface MessageOptionsProps {
  message: any;
  onEdit?: () => void;
  onDelete: () => void;
  onSendNow?: () => void;
  onPlayground?: () => void;
  showPlayground?: boolean;
  showEdit?: boolean;
  showSend?: boolean;
  messageDelivered?: boolean;
  channelId?: string;
  conversationDeliveryState?: string;
}

const MessageOptions: React.FC<MessageOptionsProps> = ({
  message,
  onEdit,
  onDelete,
  onSendNow,
  onPlayground,
  showPlayground = false,
  showEdit = true,
  showSend = true,
  messageDelivered = false,
  channelId,
  conversationDeliveryState
}) => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDarkMode = mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.user);
  const [selectedScore, setSelectedScore] = useState<number | null>(() => {
    if (message?.feedbacks && user?.uuid) {
      const feedback = message.feedbacks.find((fb: any) => fb.voter_uuid === user.uuid);
      return feedback ? feedback.score : null;
    }
    return null;
  });
  // Inline animation states for feedback actions
  const [loadingScore, setLoadingScore] = useState<1 | -1 | null>(null);
  const [errorScore, setErrorScore] = useState<1 | -1 | null>(null);
  const [hoveringThumbs, setHoveringThumbs] = useState(false);
  // Countdown state for the "Deliver Now" action
  const [sendCountdown, setSendCountdown] = useState<number>(0);
  const [countdownProgress, setCountdownProgress] = useState<number>(0);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // New state for tracking sending status
  const [isSending, setIsSending] = useState(false);
  
  const COUNTDOWN_DURATION = 3000; // 3 seconds in milliseconds

  // Reset sending state when message status changes to delivered
  useEffect(() => {
    if (message.status === 'delivered' && isSending) {
      setIsSending(false);
    }
  }, [message.status, isSending]);
  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);
  
  // Handle delete confirmation
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsConfirmingDelete(true);
    // Auto-reset after 3 seconds
    setTimeout(() => {
      setIsConfirmingDelete(false);
    }, 3000);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsConfirmingDelete(false);
    onDelete();
  };

  const handleFeedbackClick = async (score: 1 | -1) => {
    try {
      // Prevent multiple simultaneous requests
      if (loadingScore !== null) return;

      // If same score is clicked again, do nothing
      if (selectedScore === score) return;

      // Start loading animation
      setLoadingScore(score);
      setErrorScore(null);

      const existingFeedback = (message?.feedbacks && user?.uuid)
        ? message.feedbacks.find((fb: any) => fb.voter_uuid === user.uuid)
        : null;
      let response;
      if (existingFeedback) {
        const payload: FeedbackUpdate = { score, voter_uuid: user?.uuid };
        response = await feedbacksService.updateFeedback(existingFeedback.id, payload);
        const scoreText = score === 1 ? 'thumbs_up' : 'thumbs_down';
        dispatch(trackAnalytics({ action_name: `user_updated_message_feedback_to_${scoreText}`, payload: { description: `user updated message feedback to ${scoreText}` } }));
      } else {
        const payload: FeedbackCreate = {
          feedbackable_id: message.id,
          feedbackable_type: 'Message',
          score,
          voter_uuid: user?.uuid,
        };
        response = await feedbacksService.createFeedback(payload);
        const scoreText = score === 1 ? 'thumbs_up' : 'thumbs_down';
        dispatch(trackAnalytics({ action_name: `user_created_message_feedback_${scoreText}`, payload: { description: `user created message feedback ${scoreText}` } }));
      }

      const newFeedback = response.data.feedback;
      // Update local selection
      setSelectedScore(newFeedback.score);

      // Update message in redux store so UI elsewhere can reflect change
      let updatedFeedbacks = message.feedbacks ? [...message.feedbacks] : [];
      const existingIndex = updatedFeedbacks.findIndex((fb: any) => fb.voter_uuid === newFeedback.voter_uuid);
      if (existingIndex > -1) {
        updatedFeedbacks[existingIndex] = newFeedback;
      } else {
        updatedFeedbacks.push(newFeedback);
      }
      const updatedMessage = {
        ...message,
        feedbacks: updatedFeedbacks,
      };
      dispatch(addMessage(updatedMessage));

      // fetch pending chunks to update notification badge
      dispatch(fetchChunks({ chunk_type: 'rule', status: 'pending_admin_approval', per_page: 50 }));

      // Stop loading animation
      setLoadingScore(null);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setLoadingScore(null);
      setErrorScore(score);
      // Reset error after brief moment
      setTimeout(() => setErrorScore(null), 1000);
    }
  };

  // Handle the send click - starts countdown
  const handleSendClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onSendNow || isSending) return;
    
    // Start countdown
    setSendCountdown(COUNTDOWN_DURATION);
    setCountdownProgress(0);
    
    // Update progress smoothly
    const startTime = Date.now();
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, COUNTDOWN_DURATION - elapsed);
      // Progress should go from 0 to 100 as time passes (for unwrapping effect)
      const progress = (elapsed / COUNTDOWN_DURATION) * 100;
      
      setSendCountdown(remaining);
      setCountdownProgress(progress);
      
      // Check if countdown is complete
      if (elapsed >= COUNTDOWN_DURATION) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        
        // Trigger send immediately when countdown completes
        if (onSendNow && !isSending) {
          setIsSending(true);
          setSendCountdown(0);
          setCountdownProgress(100);
          
          // Call the send function
          onSendNow();
          
          // Set a 30-second timeout to reset the sending state
          // This acts as a fallback if the message status doesn't update
          setTimeout(() => {
            setIsSending(false);
          }, 30000);
        }
      }
    }, 10); // Update every 10ms for smooth animation
  };

  // Handle cancel during countdown
  const handleCancelSend = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Clear timers
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    // Reset countdown state
    setSendCountdown(0);
    setCountdownProgress(0);
  };

  return (
    <Box
      className="action-buttons"
      sx={{
        position: 'relative',
        right: 0,
        // bottom: '42px',
        padding: '4px 6px',
        borderRadius: '6px',
        width: '100%',
        opacity: 0,
        transition: 'transform 0.3s ease, opacity 0.3s ease, background-color 0.2s ease',
        display: 'flex',
        gap: '4px',
        justifyContent: 'space-between',
        // Ensure this component does not overlay higher than MUI Dialog (z-index 1300)
        zIndex: 1200,
        '&:hover': {
          opacity: '1 !important'
        },
        '& .MuiIconButton-root': {
          color: isDarkMode ? 'grey.300' : 'grey.700',
          '&:hover': {
            color: isDarkMode ? 'common.white' : 'common.black',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flexDirection: 'row',
        }}
      >
        {showPlayground && onPlayground && (
          <IconButton size="medium" onClick={onPlayground}>
            <PlayArrowIcon fontSize="medium" />
          </IconButton>
        )}
        {showEdit && onEdit && (
          <IconButton size="medium" onClick={onEdit}>
            <EditIcon fontSize="medium" />
          </IconButton>
        )}
        
        {showSend && onSendNow && !messageDelivered && message.status !== 'delivered' && (
          isSending ? (
            <Tooltip title="Confirming delivery...">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  cursor: 'not-allowed',
                }}
              >
                <CircularProgress
                  size={24}
                  sx={{
                    color: theme.palette.primary.main,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': {
                        opacity: 1,
                        transform: 'scale(1)',
                      },
                      '50%': {
                        opacity: 0.7,
                        transform: 'scale(0.95)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'scale(1)',
                      },
                    },
                  }}
                />
              </Box>
            </Tooltip>
          ) : sendCountdown > 0 ? (
            <Tooltip title="Stop send">
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={100 - countdownProgress}
                  size={40}
                  thickness={4}
                  sx={{
                    color: theme.palette.warning.main,
                    transform: 'rotate(-90deg)',
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                      transition: 'stroke-dashoffset 10ms linear',
                    },
                  }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconButton
                    size="medium"
                    onClick={handleCancelSend}
                    sx={{
                      color: theme.palette.warning.main,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                      },
                    }}
                  >
                    <CloseIcon fontSize="medium" />
                  </IconButton>
                </Box>
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title="Deliver now">
              <IconButton size="medium" onClick={handleSendClick}>
                <SendIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          )
        )}

        {/* Feedback buttons */}
        <Box
          onMouseEnter={() => setHoveringThumbs(true)}
          onMouseLeave={() => setHoveringThumbs(false)}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
        {(selectedScore === null || selectedScore === 1 || hoveringThumbs) && (
          <Tooltip title="Thumbs Up">
            <IconButton
              size="medium"
              onClick={() => handleFeedbackClick(1)}
              sx={{
                animation: loadingScore === 1 ? 'fadeBlink 1s infinite' : errorScore === 1 ? 'shake 0.4s' : undefined,
                color: errorScore === 1 ? theme.palette.error.main : undefined,
                '@keyframes fadeBlink': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                  '100%': { opacity: 1 },
                },
                '@keyframes shake': {
                  '0%,100%': { transform: 'translateX(0)' },
                  '25%': { transform: 'translateX(-2px)' },
                  '75%': { transform: 'translateX(2px)' },
                },
              }}
            >
              {selectedScore === 1 ? (
                <ThumbUpIcon fontSize="medium" />
              ) : (
                <ThumbUpAltOutlinedIcon fontSize="medium" />
              )}
            </IconButton>
          </Tooltip>
        )}
        {(selectedScore === null || selectedScore === -1 || hoveringThumbs) && (
          <Tooltip title="Thumbs Down">
            <IconButton
              size="medium"
              onClick={() => handleFeedbackClick(-1)}
              sx={{
                animation: loadingScore === -1 ? 'fadeBlink 1s infinite' : errorScore === -1 ? 'shake 0.4s' : undefined,
                color: errorScore === -1 ? theme.palette.error.main : undefined,
                '@keyframes fadeBlink': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                  '100%': { opacity: 1 },
                },
                '@keyframes shake': {
                  '0%,100%': { transform: 'translateX(0)' },
                  '25%': { transform: 'translateX(-2px)' },
                  '75%': { transform: 'translateX(2px)' },
                },
              }}
            >
              {selectedScore === -1 ? (
                <ThumbDownIcon fontSize="medium" />
              ) : (
                <ThumbDownAltOutlinedIcon fontSize="medium" />
              )}
            </IconButton>
          </Tooltip>
        )}
          {isConfirmingDelete ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: theme.palette.error.main,
                borderRadius: '4px',
                padding: '3px 8px',
                color: theme.palette.error.contrastText,
                fontSize: '0.75rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                animation: 'slideIn 0.2s forwards',
                '@keyframes slideIn': {
                  '0%': { opacity: 0, transform: 'translateX(10px)' },
                  '100%': { opacity: 1, transform: 'translateX(0)' }
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.9),
                }
              }}
              onClick={handleConfirmDelete}
            >
              Confirm
            </Box>
          ) : (
            <IconButton size="medium" onClick={handleDeleteClick}>
              <DeleteIcon fontSize="medium" />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-end' : 'center',
          gap: isMobile ? 0.5 : 1,
          maxWidth: isMobile ? '150px' : 'none',
        }}
      >
        {/* Render RescheduleDelivery if message is pending */}
        {message.status === 'pending_delivery' && (
          <RescheduleDelivery 
            message={message} 
            channelId={channelId}
            conversationDeliveryState={conversationDeliveryState}
          />
        )}
        
        {/* Show timestamp only when message is not pending delivery */}
        {message.status !== 'pending_delivery' && (
          <Typography
            variant="body2"
            className="timestamp"
            sx={{
              fontSize: isMobile ? "12px" : "14px",
              opacity: 1,
              transition: 'opacity 300ms ease-in-out',
              zIndex: 1,
              color: isDarkMode ? theme.palette.text.secondary : undefined,
              whiteSpace: 'nowrap',
              textAlign: isMobile ? 'right' : 'left',
              '&:hover': {
                opacity: 1
              }
            }}
          >
            {dateTimeFormatter(
              message.status === "delivered" && message.delivered_at 
                ? message.delivered_at 
                : (message.deliver_after || message.created_at),
              isMobile ? 'humanized_time_period' : 'day_month_ordinal_humanized_time_period'
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MessageOptions; 
